import { Badge, BadgeProps, Box, Button, Container, Header, Icon, SpaceBetween } from "@cloudscape-design/components"
import { ExtensionCardProps } from "types/extensionTypes"
import { extensionCategoryBagdeColor, formatDate, openInNewTab } from "utils/Utils"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faDesktop, faFileLines, faLink, faPuzzlePiece, faShieldHalved, faStar } from "@fortawesome/free-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { useState } from "react";
import { useCluster } from "contexts/ClusterContext";


const CATEGORY_ICON_MAP: Record<string, IconDefinition> = {
    Authentication: faShieldHalved,
    Utility: faFileLines,
    Monitoring: faDesktop,
    Integration: faLink
};


const LICENCE_BADGE_COLOR: Record<string, BadgeProps["color"]> = {
    Paid: "grey",
    Free: "green",
};



function ExtensionCard({extension, viewMode, onInstall, onUninstall}: ExtensionCardProps) {
    
    const [showAllVersions, setShowAllVersions] = useState(false);
    const { selectedClusterDetails } = useCluster();

  const isClusterActive = selectedClusterDetails?.status === "active";
    
    const MAX_VISIBLE = 3;

    const supportedVersions = extension.metadata.supportedVersions;

    const visibleVersions = showAllVersions
        ? supportedVersions
        : supportedVersions.slice(0, MAX_VISIBLE);

    const remainingCount = supportedVersions.length - MAX_VISIBLE;


    const categoryIcon = CATEGORY_ICON_MAP[extension.metadata.category] || faStar;
    const licenceColor = (LICENCE_BADGE_COLOR[extension.metadata.licenseType] ?? "green") as BadgeProps["color"]


    const isCustom = extension.organization && extension.uploadedByUser;


    const extensionUploadDate = formatDate(extension.createdAt);





    const renderFooter = () => {

        switch (viewMode) {

            case "browse-tab":
                return (
                    <div className="flex justify-space-between">
                        <Button 
                            variant="inline-link"
                            className="color-black"
                            iconName="script"
                            onClick={() => openInNewTab(extension.metadata.repository?.url)}
                        >
                            Repository
                        </Button>

                        {extension.status === "uninstalled" ? (
                            <Button 
                                variant="inline-link" 
                                iconName="add-plus"
                                disabled={!isClusterActive}
                                onClick={() => onInstall?.(extension)}
                            >
                                Install
                            </Button>
                        ) : extension.status === "pending" ? (
                            <div className="pending-field bold">
                                Pending...
                            </div>
                        ) : extension.status === "uninstalling" ? (
                            <div className="red-color bold">
                                Uninstalling...
                            </div>
                        ) : (
                            <div className="flex gap-5 verified verified-field">
                                <Icon name="status-positive" />
                                Installed
                            </div>
                        )}
                    </div>
                );


            case "installed-tab":
                return (
                    <div className="flex justify-space-between">

                        <Button 
                            variant="inline-link"
                            className="color-black"
                            iconName="script"
                            onClick={() => openInNewTab(extension.metadata.repository?.url)}
                        >
                            Repository
                        </Button>

                        {extension.status === "installed" ?
                            (
                                <div className="uninstall-btn-wrapper">
                                    <Button variant="inline-link" iconName="status-stopped" disabled={!isClusterActive} onClick={()=>onUninstall?.(extension)}>
                                        Uninstall
                                    </Button>
                                </div>
                            ):(
                                <div className="red-color bold">
                                    Uninstalling...
                                </div>
                            )
                        }
                    </div>
                );


            case "custom-tab":
                return (
                    <div className="flex flex-row-reverse justify-space-between">

                        {extension.status === "uninstalled" ? (
                            <Button
                                variant="inline-link"
                                iconName="add-plus"
                                disabled={!isClusterActive}
                                onClick={() => onInstall?.(extension)}
                            >
                                Install
                            </Button>
                        ) : extension.status === "pending" ? (
                            <div className="pending-field bold">
                                Pending...
                            </div>
                        ) : extension.status === "uninstalling" ? (
                            <div className="red-color bold">
                                Uninstalling...
                            </div>
                        ) : (
                            <>
                                <div className="uninstall-btn-wrapper">
                                    <Button 
                                        variant="inline-link" 
                                        iconName="status-stopped" 
                                        disabled={!isClusterActive}
                                        onClick={()=>onUninstall?.(extension)}
                                    >
                                        Uninstall
                                    </Button>
                                </div>

                                <div className="flex gap-5 verified-field">
                                    <Icon name="status-positive" />
                                    Installed
                                </div>
                            </>
                        )}

                    </div>
                );


            default:
                return null;
        }
    };




    
    return (
        <Container className="extensions-container"
            header={
                <div className="flex justify-space-between align-center">
                    <div className="extension-picture-container">
                        <img 
                            className="extension-picture" 
                            src={!isCustom ? extension.metadata.repository?.picture : "/assets/plugin-icon.png"} 
                            alt="Extension picture" 
                        />
                    </div>
                    
                    <div>
                        <SpaceBetween size="s" direction="horizontal">
                            {!isCustom ?
                                (<>
                                    <Badge color={extensionCategoryBagdeColor(extension.metadata.category)}>
                                        <FontAwesomeIcon
                                            icon={categoryIcon}
                                            className="margin-right-5"
                                        />
                                        {extension.metadata.category}
                                    </Badge>
                                    <Badge color={licenceColor}>{extension.metadata.licenseType}</Badge>
                                </>)
                                :
                                (<Badge color="blue">
                                    <FontAwesomeIcon
                                        icon={faPuzzlePiece}
                                        className="margin-right-5"
                                    />
                                    Custom
                                </Badge>)
                            }
                        </SpaceBetween>
                    </div>
                </div>
            }
        
            footer={renderFooter()}
        >
            <div>
                <SpaceBetween size="s">
                    <Header variant="h3">
                        {extension.displayName}
                    </Header>
                    
                    <div>
                        {extension.metadata.description}
                    </div>
                    
                    <div className="flex align-center gap-10 wrap">
                        <span className="font-size-12 kc-version-description">Compatible with:</span>

                        <SpaceBetween size="xxs" direction="horizontal">
                            {visibleVersions.map((version) => (
                                <Badge key={version}>
                                    {version}
                                </Badge>
                            ))}

                            {remainingCount > 0 && (
                                <Button
                                    variant="inline-link"
                                    className="kc-version-see-more-btn"
                                    onClick={() => setShowAllVersions(prev => !prev)}
                                >
                                    {showAllVersions
                                        ? "Show less"
                                        : `+${remainingCount} more`
                                    }
                                </Button>
                            )}
                        </SpaceBetween>
                    </div>

                    {isCustom && (
                        <SpaceBetween size="xxs">
                            <div className="font-size-12">
                                <strong>Uploaded by:</strong>{" "}
                                {extension.uploadedByUser?.firstName} {extension.uploadedByUser?.lastName}
                            </div>

                            <div className="font-size-12">
                                <strong>Uploaded on:</strong>{" "}
                                {extensionUploadDate}
                            </div>
                        </SpaceBetween>
                    )}
                </SpaceBetween>
            </div>
        </Container>
  )
}

export default ExtensionCard
